/**
 * Os resultados publicados de uma campanha, dentro do cartão (§5.8; P-47; DS-677). O mesmo bloco no cartão do
 * Cliente, no do Anunciante e no do Parceiro comissionado: o conteúdo é um só, e o interruptor decide quem o recebe.
 *
 * A forma é a da prévia do cartão no diálogo "Portal" da 5.2 (`renderPortal`): três números lado a lado, com o rótulo
 * de 11px em cima (`grid-cols-3 gap-2 mt-3`) e o número na classe `.tnum` transportada e a linha do período em 12px, no
 * CSS próprio do portal (D-134). Sem número, uma frase só: a situação dos dados, "sem números".
 *
 * O rótulo fixo ("Mídia programática das Campaigns vinculadas: …") qualifica os números e continua à vista, mas UMA vez
 * por seção, no apoio da cabeça (`RotuloDosResultados`), e não repetido em cada cartão (D-203).
 *
 * Numa coluna estreita (320 px dá uns 65 px a cada número) a contagem quebra de linha no ponto do milhar, e nunca é
 * cortada: um número pela metade é um número errado (DS-677 (h)).
 */
import { Fragment } from 'react'
import type { ResultadosDaCampanhaNoPortal } from '@dv/contracts'
import { lerResultados } from '../campanha-no-portal.js'

export function ResultadosDaCampanha({ resultados }: { resultados: ResultadosDaCampanhaNoPortal }) {
  const leitura = lerResultados(resultados)
  return (
    <div className="resultados-da-campanha" data-teste="resultados-da-campanha">
      {leitura.tipo === 'numeros' ? (
        <>
          <dl className="resultados-numeros">
            <div>
              <dt>Impressões</dt>
              <dd className="tnum" data-teste="impressoes"><QuebraNoMilhar texto={leitura.impressoes} /></dd>
            </div>
            <div>
              <dt>Cliques</dt>
              <dd className="tnum" data-teste="cliques"><QuebraNoMilhar texto={leitura.cliques} /></dd>
            </div>
            <div>
              <dt>CTR</dt>
              <dd className="tnum" data-teste="ctr">{leitura.ctr}</dd>
            </div>
          </dl>
          <p className="resultados-periodo">
            <span data-teste="periodo-dos-resultados">{leitura.periodo}</span>
            {' · '}
            <span data-teste="atualizado-em">{leitura.atualizadoEm}</span>
            {' · '}
            <span data-teste="situacao-dos-dados">{leitura.situacao}</span>
          </p>
        </>
      ) : (
        <p className="resultados-sem-numeros">
          <span data-teste="situacao-dos-dados">{leitura.situacao}</span>, sem números.
        </p>
      )}
    </div>
  )
}

/**
 * O rótulo fixo dos resultados (§5.8, P-47), UMA vez na cabeça da seção de campanhas (D-203).
 *
 * Ele diz o que os números são (só a mídia programática das Campaigns vinculadas, sem outras mídias e sem divisão por
 * produto), e por isso não some: sai do cartão, onde se repetia N vezes na mesma seção, e fica uma vez acima deles. O
 * texto é o que o servidor mandou (`rotulo`, literal do contrato), e só existe quando algum cartão da seção traz
 * resultados: sem número publicado, não há o que qualificar.
 */
export function RotuloDosResultados({
  resultados,
}: {
  resultados: readonly (ResultadosDaCampanhaNoPortal | null | undefined)[]
}) {
  const publicado = resultados.find((item): item is ResultadosDaCampanhaNoPortal => item !== null && item !== undefined)
  if (publicado === undefined) return null
  return (
    <p className="apoio-da-secao" data-teste="rotulo-dos-resultados">
      {publicado.rotulo}
    </p>
  )
}

/**
 * "12.345.678" com um ponto de quebra (`<wbr>`) depois de cada ponto do milhar: na coluna estreita a linha quebra em
 * "12.345." e "678", e não no meio de um grupo. O texto não muda (o `<wbr>` não tem texto: copiar e ler em voz alta dão
 * o número inteiro), e o `overflow-wrap:anywhere` do CSS segura o grupo que sozinho não caiba.
 */
function QuebraNoMilhar({ texto }: { texto: string }) {
  const grupos = texto.split('.')
  return grupos.map((grupo, indice) => (
    // O índice é a posição do grupo no número: a lista é refeita inteira a cada texto, nunca reordenada.
    <Fragment key={indice}>
      {grupo}
      {indice < grupos.length - 1 && <>.<wbr /></>}
    </Fragment>
  ))
}
